'use-client';

import React from 'react';
import {
  Drawer as ShadcnDrawer,
  DrawerContent as ShadcnDrawerContent,
  DrawerDescription as ShadcnDrawerDescription,
  DrawerFooter as ShadcnDrawerFooter,
  DrawerHeader as ShadcnDrawerHeader,
  DrawerTitle as ShadcnDrawerTitle,
} from '@/lib/primitives/shadcn/drawer';
import classNames from 'classnames';

/* ================================== TYPES ================================= */

export type TDrawerProps = {
  title?: string;
  description?: string;
  isOpen?: boolean;
  footer?: React.ReactNode;
  children?: React.ReactNode;
  onClose: () => void;
  container?: HTMLElement;
  backgroundColor?: string;
  isTransparent?: boolean;
  useBoxShadow?: boolean;
  className?: {
    title?: string;
    description?: string;
  };
};

/* ========================================================================== */
/*                                  COMPONENT                                 */
/* ========================================================================== */
export function Drawer(props: TDrawerProps) {
  const {
    title,
    description,
    isOpen = false,
    footer,
    onClose,
    container,
    children,
    backgroundColor = 'white',
    isTransparent = false,
    useBoxShadow = false,
    className,
  } = props;

  const isUsingHeader = title || description;

  const drawerContentClasses = classNames(
    isTransparent && 'bg-opacity-30',
    isTransparent && 'backdrop-blur-md',
    backgroundColor === 'infinity' ? 'bg-infinity' : `bg-${backgroundColor}`,
  );

  return (
    <ShadcnDrawer
      dismissible
      open={isOpen}
      onClose={onClose}
      container={container}
    >
      <ShadcnDrawerContent
        className={drawerContentClasses}
        style={{
          boxShadow: useBoxShadow
            ? '0 0 50px rgba(255, 255, 255, 0.5)'
            : 'none',
        }}
      >
        {isUsingHeader && (
          <ShadcnDrawerHeader>
            {title && (
              <ShadcnDrawerTitle className={className?.title}>
                {title}
              </ShadcnDrawerTitle>
            )}
            {description && (
              <ShadcnDrawerDescription className={className?.description}>
                {description}
              </ShadcnDrawerDescription>
            )}
          </ShadcnDrawerHeader>
        )}
        {children && <div className="p-4">{children}</div>}
        {footer && <ShadcnDrawerFooter>{footer}</ShadcnDrawerFooter>}
      </ShadcnDrawerContent>
    </ShadcnDrawer>
  );
}
